import {
  Text,
  Button,
  GreyGrey40,
  GreyGrey60,
  GreyGrey80,
  Primary13Regular,
  Primary13SemiBold,
  Primary20SemiBold,
  RedRed70,
  Skeleton,
  IconChevronLeft,
  IconChevronRight,
  Flex
} from '@sequencehq/core-components'
import { toMoney } from '@sequencehq/utils'
import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'
import { inflect } from 'inflection'
import { FC } from 'react'

type CurrencyTotals =
  Dashboardv99990101Api.GetInvoiceSummary.InvoiceSummaryCurrencyTotals
type Currency = CurrencyTotals['currency']

type Props = {
  label: string
  totals: CurrencyTotals[]
  // The active currency is owned by the parent so every card shows the same
  // currency at once. Undefined only while data is still loading.
  activeCurrency?: Currency
  showCurrencyNav: boolean
  // Include the currency code in the footer ("12 GBP invoices") when the
  // account has more than one currency enabled.
  showCurrencyInFooter: boolean
  onPreviousCurrency: () => void
  onNextCurrency: () => void
  isLoading: boolean
  emphasize?: boolean
  'data-testid'?: string
}

export const InvoiceSummaryStatCard: FC<Props> = ({
  label,
  totals,
  activeCurrency,
  showCurrencyNav,
  showCurrencyInFooter,
  onPreviousCurrency,
  onNextCurrency,
  isLoading,
  emphasize = false,
  'data-testid': dataTestId
}) => {
  const childTestId = (suffix: string) =>
    dataTestId ? `${dataTestId}.${suffix}` : undefined

  const activeTotals = totals.find(total => total.currency === activeCurrency)
  const invoiceCount = activeTotals?.invoiceCount ?? 0
  const currencyLabel =
    showCurrencyInFooter && activeCurrency ? `${activeCurrency} ` : ''
  const footer = `${invoiceCount} ${currencyLabel}${inflect('invoice', invoiceCount)}`
  // A card may have no invoices in the active currency — show a zero total in
  // that currency rather than a dash, so all cards stay visually aligned.
  const amount = activeCurrency
    ? toMoney({
        value: String(activeTotals?.grossTotal ?? 0),
        currency: activeCurrency
      })
    : '—'

  return (
    <Flex
      role="group"
      position="relative"
      flex={1}
      flexDirection="column"
      gap="8px"
      padding="16px"
      borderRadius="8px"
      border={`1px solid ${GreyGrey40}`}
      backgroundColor="white"
      data-testid={dataTestId}
    >
      {showCurrencyNav && (
        <Flex
          position="absolute"
          top="8px"
          right="8px"
          gap="2px"
          opacity={0}
          _groupHover={{ opacity: 1 }}
        >
          <Button
            variant="ghost"
            size="24px"
            aria-label="Previous currency"
            icon={<IconChevronLeft />}
            onClick={onPreviousCurrency}
            data-testid={childTestId('previous')}
          />
          <Button
            variant="ghost"
            size="24px"
            aria-label="Next currency"
            icon={<IconChevronRight />}
            onClick={onNextCurrency}
            data-testid={childTestId('next')}
          />
        </Flex>
      )}
      <Text
        {...Primary13SemiBold}
        color={GreyGrey60}
        data-testid={childTestId('title')}
      >
        {label}
      </Text>
      {isLoading ? (
        <Skeleton height="24px" width="120px" />
      ) : (
        <Text
          {...Primary20SemiBold}
          color={emphasize ? RedRed70 : GreyGrey80}
          data-testid={childTestId('amount')}
        >
          {amount}
        </Text>
      )}
      {isLoading ? (
        <Skeleton height="16px" width="64px" />
      ) : (
        <Text
          {...Primary13Regular}
          color={GreyGrey60}
          data-testid={childTestId('footer')}
        >
          {footer}
        </Text>
      )}
    </Flex>
  )
}
